import { uniqBy } from 'lodash'
import { useEffect, useMemo, useRef, useState } from 'react'

import { InputCodeManualGen } from '@/components'
import { ModalForm } from '@/components/Modal/ModalForm'
import {
	Form,
	FormField,
	SelectFormField,
	TextAreaFormField,
	TextFormField,
} from '@/components/UberForm'
import {
	formatStructureOptionLabel,
	useAppContext,
	useDebounce,
	useStructureSearch,
} from '@/hooks'
import {
	CustomMenuList,
	CustomOption,
} from '@/lib/components/Select/CustomSelectComponents'
import { useSelectedBranchNumericId } from '@/pages/User/pages/Home/components/TreePanel/TreePanelContext'
import {
	useCreateRelationRelationMutation,
	useUpdateRelationRelationMutation,
} from '@/rtkApi'
import { StructureDto } from '@/rtkApi/mddApiSliceGenerated'
import { RelationDto, StructureType } from '@/rtkApi/mddApiSliceGeneratedV2'
import { enumToValueLabelCollection } from '@/utils/collections'
import { getApiErrorDescription, logErrorAndNotifyWithToast } from '@/utils/errorHandling'

import { SearchEnum } from '../types'

type DependenciesModalProps = {
	dependency?: Partial<RelationDto>
	node: StructureDto
	onClose: () => void
	onSave: () => void
}

export const DependenciesModal = ({
	node,
	dependency,
	onSave,
	onClose,
}: DependenciesModalProps) => {
	const { t } = useAppContext()
	const branchId = useSelectedBranchNumericId()
	const [createRelation] = useCreateRelationRelationMutation()
	const [updateRelation] = useUpdateRelationRelationMutation()
	const formRef = useRef<Form<RelationDto & { rightSideStructure: number; searchElementType: string }>>(null)

	const [search, setSearch] = useState(
		dependency?.rightSideStructure?.name ?? '',
	)

	const [searchType, setSearchType] = useState<StructureType>(
		(dependency?.rightSideStructure?.type as StructureType) ?? 'TABLE',
	)

	const searchDebounced = useDebounce(search, 300)

	const [name, setName] = useState(dependency?.name ?? '')

	const { loadOptions, searchOptions, isFetching } = useStructureSearch({
		types: [searchType],
	})

	// Load initial options on mount and when search type changes
	useEffect(() => {
		loadOptions()
	}, [searchType])

	// Search when user types
	useEffect(() => {
		if (searchDebounced) {
			loadOptions(searchDebounced)
		}
	}, [searchDebounced])

	const handleSubmit = async (
		form: Partial<RelationDto> & {
			rightSideStructure?: number
			searchElementType: string
		},
	) => {
		const isFormValid = formRef?.current?.state.valid

		if (!isFormValid || branchId == null) {
			return
		}

		const foundStructure = searchOptions.find(
			(structure) => structure.id === form.rightSideStructure,
		)
		const rightSideStructure = {
			code: foundStructure?.code,
			id: foundStructure?.id,
			name: foundStructure?.name,
			type: foundStructure?.type,
		} as RelationDto['rightSideStructure']

		try {
			if (dependency?.id != null) {
				await updateRelation({
					branchId,
					relationDto: {
						...dependency,
						code: form.code,
						description: form.description,
						name: form.name,
						rightSideStructure,
					},
					relationId: dependency.id,
					structureId: node.id,
				}).unwrap()
			} else {
				await createRelation({
					branchId,
					relationCreateDto: {
						code: form.code,
						description: form.description,
						name: form.name,
						rightSideStructure,
					},
					structureId: node.id,
				}).unwrap()
			}
			onSave()
			onClose()
		} catch (error) {
			logErrorAndNotifyWithToast({
				context: { operation: 'DependenciesModal.save' },
				detail: getApiErrorDescription(error),
				error,
				messageKey:
					dependency?.id != null
						? 'UPDATE_DEPENDENCY_FAILED'
						: 'CREATE_DEPENDENCY_FAILED',
				t,
			})
		}
	}

	const elements = useMemo(() => {
		if (dependency?.rightSideStructure) {
			return uniqBy(
				searchOptions.concat({
					id: dependency.rightSideStructure?.id,
					name: dependency.rightSideStructure?.name,
					fullPath: dependency.rightSideStructure?.fullPath,
				}),
				(value) => value.id,
			)
		}

		return searchOptions
	}, [dependency, searchOptions])

	return (
		<>
			<ModalForm<
				RelationDto & { rightSideStructure: number; searchElementType: string }
			>
				open={true}
				onClose={onClose}
				contentStyle={{
					width: '550px',
				}}
				initialValues={
					dependency
						? {
								...dependency,
								searchElementType: searchType,
								rightSideStructure: dependency.rightSideStructure?.id,
							}
						: { searchElementType: searchType }
				}
				header={dependency ? t('UPDATE_DEPENDENCY') : t('CREATE_DEPENDENCY')}
				onSubmit={handleSubmit}
				formRef={formRef}
			>
				<TextFormField
					title={t('NAME')}
					name="name"
					required
					onChange={(v) => setName(v as string)}
				/>
				<FormField name="code" title={t('CODE')}>
					<InputCodeManualGen nameValue={name} />
				</FormField>
				<TextAreaFormField title={t('DESCRIPTION')} name="description" />
				<SelectFormField
					title={t('SEARCH_ELEMENT_TYPE')}
					name="searchElementType"
					options={enumToValueLabelCollection(SearchEnum)}
					required
					clearable={false}
					onChange={(value) => setSearchType(value)}
				/>
				<SelectFormField
					title={t('ELEMENT')}
					name="rightSideStructure"
					options={elements}
					fieldIsLoading={isFetching}
					valueKey="id"
					required
					labelKey="name"
					clearable={false}
					onInputChange={(search) => {
						if (search) {
							setSearch(search)
						}
					}}
					isNumeric
					placeholder={t('SEARCH')}
					formatOptionLabel={formatStructureOptionLabel}
					components={{ Option: CustomOption, MenuList: CustomMenuList }}
				/>
			</ModalForm>
		</>
	)
}
